import type { ReactElement } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useLaborActiveSessionContext } from "@/components/LaborActiveSessionProvider";
import { setLaborShellLockdownBypassed } from "@/lib/labor/laborEnv";
import { releaseLaborHubPresence } from "@/lib/labor/laborReleaseHubPresence";
import type { LaborPresence } from "@/lib/labor/laborPresence";
import {
  clockedInElsewhereBody,
  clockedInElsewhereClockOutLabel,
  clockedInElsewhereKeepReadOnlyLabel,
} from "@/pages/clockedInElsewhereCopy";
import {
  publishLaborReadOnlyStayRequest,
  withdrawLaborReadOnlyStayRequest,
} from "@/lib/labor/laborReadOnlyStay";
import { formatLaborLocationLabel } from "@/pages/clampqc/clampQcLaborClockState";

export function ClockedInElsewherePage({ presence }: { presence: LaborPresence }): ReactElement {
  const queryClient = useQueryClient();
  const { refetch: refetchOpenSession } = useLaborActiveSessionContext();
  const stationLabel = formatLaborLocationLabel(presence.page ?? "");
  const mobileConflict = presence.conflictSource === "mobile";

  const clockOut = useMutation({
    mutationFn: async (keepReadOnly: boolean) => {
      if (keepReadOnly) {
        publishLaborReadOnlyStayRequest({ page: presence.page, tabId: presence.tabId });
      } else {
        withdrawLaborReadOnlyStayRequest();
      }
      try {
        await releaseLaborHubPresence(queryClient);
      } catch (error) {
        if (keepReadOnly) withdrawLaborReadOnlyStayRequest();
        throw error;
      }
    },
    onSuccess: () => {
      void refetchOpenSession();
    },
  });

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>You are clocked in at {stationLabel}.</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600">{clockedInElsewhereBody(mobileConflict)}</p>
          {clockOut.isError ? (
            <p className="text-sm text-rose-700">
              {clockOut.error instanceof Error ? clockOut.error.message : "Clock out failed."}
            </p>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              type="button"
              onClick={() => clockOut.mutate(false)}
              disabled={clockOut.isPending}
              className="w-full sm:w-auto"
            >
              {clockedInElsewhereClockOutLabel(mobileConflict, clockOut.isPending && clockOut.variables === false)}
            </Button>
            {mobileConflict ? null : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => clockOut.mutate(true)}
                disabled={clockOut.isPending}
                className="w-full sm:w-auto"
              >
                {clockedInElsewhereKeepReadOnlyLabel(
                  stationLabel,
                  clockOut.isPending && clockOut.variables === true,
                )}
              </Button>
            )}
            {import.meta.env.DEV ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setLaborShellLockdownBypassed(true)}
                className="w-full sm:w-auto"
              >
                Continue browsing (development)
              </Button>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
